<template>
    <div id="list-product-types-component" class="w-full h-full">
        <div class="flex items-end gap-2 w-full">
            <div class="flex flex-col w-full">
                <div class="flex items-center justify-between gap-2 mb-1">
                    <label
                        class="block mb-1 text-sm text-theme-muted font-semibold"
                        >Tipo de producto</label
                    >
                    <a
                        v-if="$ability.hasPermission('tenant:GetProductTypes')"
                        href="/app/settings/catalog"
                        target="_blank"
                        rel="noopener"
                        class="text-xs text-theme-primary hover:underline whitespace-nowrap"
                    >
                        Editar tipos por defecto
                    </a>
                </div>

                <button
                    type="button"
                    class="flex items-center w-full text-left border rounded-md bg-theme-input px-3 py-2 text-sm border-theme-border"
                    :class="
                        enabled
                            ? 'cursor-pointer hover:bg-theme-hover'
                            : 'cursor-not-allowed opacity-60'
                    "
                    :disabled="!enabled"
                    @click="enabled && (showInfo = true)"
                >
                    <span>
                        {{
                            $data.$productTypes[itemCurrent] ||
                            $data.$productTypes.single
                        }}
                    </span>
                </button>
            </div>

            <button
                class="flex-shrink-0 w-9 h-9 rounded-full bg-brand-blue flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                type="button"
                @click="showInfo = true"
            >
                <span class="text-sm font-bold leading-none">?</span>
            </button>
        </div>
        <div class="flex items-center gap-2 flex-wrap mt-1">
            <template v-if="currentVariantFields.length">
                <span class="text-xs text-theme-muted">Permite variantes:</span>
                <span
                    v-for="field in currentVariantFields"
                    :key="field"
                    class="text-xs px-2 py-0.5 rounded-full bg-theme-hover text-theme-muted whitespace-nowrap"
                >
                    {{ field }}
                </span>
            </template>
            <span
                v-else
                class="text-xs px-2 py-0.5 rounded-full bg-theme-hover text-theme-muted whitespace-nowrap"
            >
                Sin variantes
            </span>
            <p
                v-if="isCurrentTypeDisallowed"
                class="text-xs text-theme-warning"
            >
                Este tipo ya no está habilitado para nuevos productos
            </p>
        </div>

        <app-dialog v-model="showInfo">
            <template #header>
                <h4 class="text-lg font-semibold text-theme-base">
                    Tipos de producto
                </h4>
            </template>

            <div class="flex flex-col gap-3 py-2">
                <button
                    v-for="(info, key) in filteredDescriptions"
                    :key="key"
                    type="button"
                    class="flex flex-col gap-1 p-3 rounded-lg border transition-colors text-left"
                    :class="[
                        key === option
                            ? 'border-brand-blue bg-brand-blue'
                            : 'border-theme-border bg-theme-card',
                        enabled
                            ? 'cursor-pointer hover:opacity-90'
                            : 'cursor-default',
                        { 'opacity-60': isLocked(key) && key !== option }
                    ]"
                    @click="selectOption(key)"
                >
                    <div class="flex items-center justify-between gap-2">
                        <span
                            class="font-semibold"
                            :class="
                                key === option
                                    ? 'text-white'
                                    : 'text-theme-base'
                            "
                        >
                            <span v-if="isLocked(key) && key !== option"
                                >🔒</span
                            >
                            {{ info.label }}
                        </span>
                        <div class="flex flex-wrap gap-1 justify-end">
                            <span
                                v-for="field in variantFieldsFor(key)"
                                :key="field"
                                class="text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                                :class="
                                    key === option
                                        ? 'bg-white text-brand-blue'
                                        : 'bg-theme-hover text-theme-muted'
                                "
                            >
                                {{ field }}
                            </span>
                            <span
                                v-if="!variantFieldsFor(key).length"
                                class="text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                                :class="
                                    key === option
                                        ? 'bg-white text-brand-blue'
                                        : 'bg-theme-hover text-theme-muted'
                                "
                            >
                                Sin variantes
                            </span>
                        </div>
                    </div>
                    <span
                        class="text-sm"
                        :class="
                            key === option ? 'text-white' : 'text-theme-muted'
                        "
                    >
                        {{ info.description }}
                    </span>
                </button>
            </div>

            <template #footer>
                <div class="flex items-center justify-between w-full">
                    <wiki-learn-more-link topic="tipos-de-producto" />
                    <app-button flat @click="showInfo = false">
                        Cerrar
                    </app-button>
                </div>
            </template>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import { canShowUpgrade } from "@/common/fields/plan-features";
import {
    PRODUCT_VARIANTS_FEATURE,
    PRODUCT_VARIANTS_PERMISSION,
    getVariantFields,
    productTypeDescriptions,
    productTypes,
    typeRequiresVariants
} from "@/common/fields/product-types";

import AppDialog from "@/components/utils/AppDialog.vue";
import WikiLearnMoreLink from "@/components/utils/WikiLearnMoreLink.vue";

export default {
    name: "TypeProductSelector",
    components: { AppDialog, WikiLearnMoreLink },
    model: {
        prop: "itemCurrent",
        event: "change"
    },
    props: {
        itemCurrent: {
            default: () => "single",
            type: String,
            require: true
        },
        enabled: {
            default: () => true,
            type: Boolean,
            required: false
        },
        allowedTypes: {
            default: () => [],
            type: Array,
            required: false
        }
    },
    emits: ["change"],
    data: () => ({
        option: "",
        showInfo: false,
        $productTypes: productTypes,
        $productTypeDescriptions: productTypeDescriptions
    }),
    computed: {
        ...mapState("auth", ["permissions"]),
        hasFilter() {
            return this.allowedTypes.length > 0;
        },
        isCurrentTypeDisallowed() {
            return (
                this.hasFilter &&
                this.option &&
                !this.allowedTypes.includes(this.option)
            );
        },
        currentVariantFields() {
            return getVariantFields(this.itemCurrent);
        },
        filteredDescriptions() {
            if (!this.hasFilter) return this.$data.$productTypeDescriptions;
            const allowed = new Set(this.allowedTypes);
            if (this.option) allowed.add(this.option);
            return Object.fromEntries(
                Object.entries(this.$data.$productTypeDescriptions).filter(
                    ([key]) => allowed.has(key)
                )
            );
        }
    },
    watch: {
        itemCurrent(value) {
            this.setInput(value);
        }
    },
    mounted() {
        this.setInput(this.itemCurrent);
    },
    methods: {
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        isLocked(key) {
            return (
                typeRequiresVariants(key) &&
                !this.$ability.hasPermission(PRODUCT_VARIANTS_PERMISSION)
            );
        },
        selectOption(key) {
            if (!this.enabled) return;
            if (this.isLocked(key) && key !== this.option) {
                if (
                    canShowUpgrade(this.permissions, PRODUCT_VARIANTS_FEATURE)
                ) {
                    this.showInfo = false;
                    this.openPlanUpgrade({
                        kind: "feature",
                        feature: PRODUCT_VARIANTS_FEATURE
                    });
                }
                return;
            }
            this.option = key;
            this.showInfo = false;
            this.$emit("change", key);
        },
        setInput(value) {
            this.option = value;
        },
        variantFieldsFor(key) {
            return getVariantFields(key);
        }
    }
};
</script>
